import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isArray, isEmpty } from 'lodash';

import patchStudio from 'Api/studio/patchStudio';
import { useToast } from 'Store/toast';
import { useLoader } from 'Store/loader';
import { studioUpsertDomainText } from 'Const/studio/text';
import { useStudioState } from 'View/studio/state';

import upsertStudioPayload from './model/request/upsertStudioPayload';

const usePatchStudio = (resetFormData) => {
  const { onOpenToast } = useToast();
  const { onShowLoader, onResetLoader } = useLoader();
  const { updateState, resetState, resetFilterState } = useStudioState();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data, id }) => {
      onShowLoader({ variant: 'loader-primary' });

      const payload = upsertStudioPayload(data);
      return await patchStudio({ payload, id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['studio'] });

      onOpenToast({
        status: 'success',
        description: studioUpsertDomainText?.successDescription,
        duration: 5,
        alwaysShow: false,
      });

      resetState();
      resetFormData();
      resetFilterState();
    },
    onError: (error) => {
      console.log(error);
      const errorFields = error?.response?.data?.errorFields;
      const errorMessage = isArray(error?.response?.data) ? error?.response?.data?.errors[0]?.message : studioUpsertDomainText?.errorDescription;

      onOpenToast({
        status: 'error',
        description: studioUpsertDomainText?.errorDescription,
        duration: 5,
        alwaysShow: false,
      });

      if (!isEmpty(errorFields)) return updateState({ errorFields });

      updateState({
        isOpenFormUpsert: false,
        errorFields: {},
        errorProps: {
          isOpen: true,
          type: 'error',
          title: studioUpsertDomainText?.errorTitle,
          description: errorMessage,
          onClose: () => updateState({ errorProps: {} }),
          marginBottom: '12px',
        },
      });
    },
    onSettled: () => {
      onResetLoader();
    },
  });
};

export default usePatchStudio;
